import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useSoftphone } from '../../hooks/useSoftphone';
import api from '../../services/api';
import {
  Phone, PhoneOff, PhoneIncoming, PhoneMissed, PhoneCall,
  Mic, MicOff, PauseCircle, PlayCircle, Hash, Delete,
  Search, Clock, Users, Copy, Check, Wifi, WifiOff,
  ChevronRight, ArrowDownLeft, ArrowUpRight, PhoneForwarded, UserPlus, X, SlidersHorizontal
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import AudioSettings from '../softphone/AudioSettings';

const DTMF_KEYS = [
  { key: '1', sub: '' }, { key: '2', sub: 'ABC' }, { key: '3', sub: 'DEF' },
  { key: '4', sub: 'GHI' }, { key: '5', sub: 'JKL' }, { key: '6', sub: 'MNO' },
  { key: '7', sub: 'PQRS' }, { key: '8', sub: 'TUV' }, { key: '9', sub: 'WXYZ' },
  { key: '*', sub: '' }, { key: '0', sub: '+' }, { key: '#', sub: '' },
];

function useCallTimer(active) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (!active) { setSecs(0); return; }
    const t = setInterval(() => setSecs(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
  const m = String(Math.floor(secs/60)).padStart(2,'0');
  const s = String(secs%60).padStart(2,'0');
  return m + ':' + s;
}

const REG_STATUS = {
  registered:   { label: 'Registrado',    color: 'text-green-400',  bg: 'bg-green-500/10 border-green-800', icon: Wifi },
  registering:  { label: 'Conectando...', color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-800', icon: Wifi },
  unregistered: { label: 'Desconectado',  color: 'text-gray-400',   bg: 'bg-gray-800/50 border-gray-700',   icon: WifiOff },
  error:        { label: 'Error SIP',     color: 'text-red-400',    bg: 'bg-red-500/10 border-red-800',     icon: WifiOff },
};

function CdrRow({ row, onDial }) {
  const isInbound = row.calltype === 'inbound';
  const missed = row.disposition === 'NO ANSWER' || row.disposition === 'FAILED';
  const num = isInbound ? row.src : row.dst;
  return (
    <button onClick={() => onDial(num)}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-800/60 transition-colors text-left group">
      <div className={['w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0',
        missed ? 'bg-red-900/40' : isInbound ? 'bg-blue-900/40' : 'bg-green-900/40'].join(' ')}>
        {missed ? <PhoneMissed size={16} className="text-red-400"/>
          : isInbound ? <ArrowDownLeft size={16} className="text-blue-400"/>
          : <ArrowUpRight size={16} className="text-green-400"/>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-200 truncate">{num || 'Desconocido'}</div>
        <div className="text-xs text-muted">
          {row.calldate ? format(new Date(row.calldate), 'd MMM, HH:mm', { locale: es }) : '—'}
        </div>
      </div>
      <Phone size={15} className="text-muted group-hover:text-green-400 transition-colors flex-shrink-0"/>
    </button>
  );
}

function ContactRow({ contact, onDial }) {
  const num = contact.phone_number || contact.extension_number;
  return (
    <button onClick={() => onDial(num)}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-800/60 transition-colors text-left group">
      <div className="w-9 h-9 rounded-full bg-blue-700 flex items-center justify-center flex-shrink-0 text-sm font-bold text-white">
        {contact.first_name ? contact.first_name[0] : '?'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-200 truncate">{contact.first_name} {contact.last_name}</div>
        <div className="text-xs text-muted">{num}</div>
      </div>
      <ChevronRight size={15} className="text-muted group-hover:text-blue-400 transition-colors flex-shrink-0"/>
    </button>
  );
}

function SipConfigPanel({ credentials }) {
  const [copied, setCopied] = useState(null);
  const host = window.location.hostname;
  const copy = (val, key) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };
  const fields = [
    { label: 'Servidor SIP', value: host, key: 'server' },
    { label: 'Puerto SIP', value: '5060', key: 'port' },
    { label: 'Usuario / Extension', value: credentials ? credentials.number : '—', key: 'user' },
    { label: 'Contrasena SIP', value: credentials ? credentials.sip_password : '—', key: 'pass' },
    { label: 'Auth ID', value: credentials ? credentials.number : '—', key: 'auth' },
    { label: 'Transporte', value: 'UDP / TCP', key: 'transport' },
  ];
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted">Usa estas credenciales para configurar el softphone de 3CX (iOS/Android/Desktop) u otro cliente SIP externo.</p>
      <div className="space-y-2">
        {fields.map(f => (
          <div key={f.key} className="flex items-center justify-between bg-gray-800/60 rounded-2xl px-4 py-3">
            <div>
              <div className="text-xs text-muted">{f.label}</div>
              <div className="text-sm text-gray-200 font-mono">{f.value}</div>
            </div>
            <button onClick={() => copy(f.value, f.key)} aria-label={`Copiar ${f.label}`} className="p-1.5 rounded-full text-muted hover:text-gray-300 hover:bg-gray-700 transition-colors">
              {copied === f.key ? <Check size={14} className="text-green-400"/> : <Copy size={14}/>}
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 p-4 bg-blue-900/20 border border-blue-800/40 rounded-2xl">
        <p className="text-xs text-blue-300 font-medium mb-1">3CX Softphone App</p>
        <p className="text-xs text-gray-400">
          En la app 3CX: Agregar cuenta → Configuracion manual → introduce los datos de arriba.
          El softphone WebRTC integrado en el navegador ya esta activo en el panel inferior derecho.
        </p>
      </div>
    </div>
  );
}

export default function DialerPage() {
  const {
    regState, callState, currentCall, isMuted, isHeld, credentials,
    call, answer, hangup, mute, hold, sendDTMF, transfer,
    secondCall, startConference, endConference,
  } = useSoftphone();

  const [dialNumber, setDialNumber] = useState('');
  const [showDTMF, setShowDTMF] = useState(false);
  const [tab, setTab] = useState('recents');
  const [recents, setRecents] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const duration = useCallTimer(callState === 'active');

  useEffect(() => {
    if (tab === 'recents') api.get('/cdr?limit=30').then(r => setRecents((r.data && r.data.data) || [])).catch(() => {});
    if (tab === 'contacts') api.get('/phonebook').then(r => setContacts((r.data && r.data.data) || [])).catch(() => {});
  }, [tab]);

  const handleKey = useCallback((k) => {
    if (callState === 'active') sendDTMF(k);
    else setDialNumber(p => p + k);
  }, [callState, sendDTMF]);

  const handleDial = useCallback((num) => {
    const target = num || dialNumber;
    if (!target) return;
    if (regState !== 'registered') {
      toast.error('Softphone no registrado. Verifica tu conexión SIP.');
      return;
    }
    if (callState !== 'idle') return;
    call(target);
    if (num) setDialNumber(num);
  }, [dialNumber, regState, callState, call]);

  // Teclado físico — captura dígitos, Backspace y Enter
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (/^[0-9*#]$/.test(e.key)) {
        handleKey(e.key);
      } else if (e.key === 'Backspace') {
        if (callState === 'idle') setDialNumber(p => p.slice(0, -1));
      } else if (e.key === 'Enter') {
        if (callState === 'idle' && dialNumber) handleDial(dialNumber);
      } else if (e.key === 'Escape') {
        if (callState !== 'idle') hangup();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleKey, handleDial, dialNumber, callState, hangup]);

  const reg = REG_STATUS[regState] || REG_STATUS.unregistered;
  const RegIcon = reg.icon;
  const filtered = contacts.filter(c => {
    if (!contactSearch) return true;
    const text = (c.first_name || '') + ' ' + (c.last_name || '') + ' ' + (c.phone_number || '');
    return text.toLowerCase().includes(contactSearch.toLowerCase());
  });

  const displayNum = callState !== 'idle' ? (currentCall ? currentCall.number : '...') : dialNumber;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Marcador</h1>
          <p className="text-sm text-muted mt-0.5">Softphone WebRTC integrado</p>
        </div>
        <div className={['flex items-center gap-2 px-4 py-2 rounded-full border text-sm', reg.bg].join(' ')}>
          <RegIcon size={14} className={reg.color}/>
          <span className={reg.color}>{reg.label}</span>
          {credentials && credentials.number && <span className="text-muted">· Ext. {credentials.number}</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card space-y-4">
          <div className={['rounded-3xl p-5 text-center min-h-[88px] flex flex-col justify-center transition-all',
            callState === 'active' ? 'bg-green-900/20 border border-green-800/40'
              : callState === 'ringing' ? 'bg-blue-900/20 border border-blue-800/40 animate-pulse'
              : callState === 'held' ? 'bg-yellow-900/20 border border-yellow-800/40'
              : 'bg-gray-800/60'].join(' ')}>
            {callState === 'idle' ? (
              <input
                aria-label="Numero a marcar"
                value={dialNumber}
                onChange={(e) => setDialNumber(e.target.value.replace(/[^0-9*#+]/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter' && dialNumber) handleDial(dialNumber); }}
                placeholder="Ingresa un numero"
                autoFocus
                className="w-full bg-transparent text-center text-3xl font-mono font-light text-white tracking-wider min-h-[40px] outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg placeholder:text-muted placeholder:text-xl"
              />
            ) : (
              <div className="text-3xl font-mono font-light text-white tracking-wider min-h-[40px]">
                {displayNum}
              </div>
            )}
            <div className="text-sm mt-1">
              {callState === 'active' && <span className="text-green-400">{duration} · En llamada</span>}
              {callState === 'calling' && <span className="text-blue-400">Marcando...</span>}
              {callState === 'ringing' && <span className="text-blue-300 animate-pulse">Llamada entrante</span>}
              {callState === 'held' && <span className="text-yellow-400">En espera</span>}
            </div>
          </div>

          {callState === 'ringing' && (
            <div className="flex gap-3">
              {/* green-600 con texto blanco medía 3.3:1, bajo 4.5:1
                  (/impeccable critique, 30-sep-2026 — P1); green-700 cumple. */}
              <button onClick={answer} className="flex-1 h-12 bg-green-700 hover:bg-green-800 text-white rounded-full flex items-center justify-center gap-2 font-medium">
                <PhoneIncoming size={20}/> Contestar
              </button>
              <button onClick={hangup} className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center gap-2 font-medium">
                <PhoneOff size={20}/> Rechazar
              </button>
            </div>
          )}

          {(callState === 'idle' || (callState === 'active' && showDTMF)) && (
            <div className="grid grid-cols-3 gap-3 justify-items-center">
              {DTMF_KEYS.map(function(item) { return (
                <button key={item.key} onClick={() => handleKey(item.key)}
                  className="h-16 w-16 bg-gray-800 hover:bg-gray-700 active:bg-gray-600 rounded-full flex flex-col items-center justify-center transition-colors select-none">
                  <span className="text-xl font-medium text-white">{item.key}</span>
                  {/* text-[11px] medía exactamente 9px, bajo el piso de 11px
                      (/impeccable critique, 30-sep-2026 — P1). */}
                  {item.sub && <span className="text-[11px] text-gray-400 tracking-widest">{item.sub}</span>}
                </button>
              ); })}
            </div>
          )}

          {(callState === 'active' || callState === 'held') && (
            <div className="grid grid-cols-3 gap-2">
              <button onClick={mute} className={['flex flex-col items-center gap-1.5 p-3 rounded-2xl transition-colors text-xs font-medium',
                isMuted ? 'bg-red-600/30 text-red-400 border border-red-800/40' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'].join(' ')}>
                {isMuted ? <MicOff size={20}/> : <Mic size={20}/>}
                {isMuted ? 'Silenciado' : 'Silenciar'}
              </button>
              <button onClick={hold} className={['flex flex-col items-center gap-1.5 p-3 rounded-2xl transition-colors text-xs font-medium',
                isHeld ? 'bg-yellow-600/30 text-yellow-400 border border-yellow-800/40' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'].join(' ')}>
                {isHeld ? <PlayCircle size={20}/> : <PauseCircle size={20}/>}
                {isHeld ? 'Reanudar' : 'En espera'}
              </button>
              <button onClick={() => setShowDTMF(function(s) { return !s; })}
                className={['flex flex-col items-center gap-1.5 p-3 rounded-2xl transition-colors text-xs font-medium',
                  showDTMF ? 'bg-blue-600/30 text-blue-400 border border-blue-800/40' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'].join(' ')}>
                <Hash size={20}/> Teclado
              </button>
            </div>
          )}

          {callState === 'active' && !secondCall && (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { const d = window.prompt('Transferir llamada a (extension o numero):'); if (d) transfer(d); }}
                className="flex flex-col items-center gap-1.5 p-3 rounded-2xl transition-colors text-xs font-medium bg-gray-800 text-gray-300 hover:bg-gray-700">
                <PhoneForwarded size={20}/> Transferir
              </button>
              <button
                onClick={() => { const d = window.prompt('Agregar a la llamada (extension o numero):'); if (d) startConference(d); }}
                className="flex flex-col items-center gap-1.5 p-3 rounded-2xl transition-colors text-xs font-medium bg-gray-800 text-gray-300 hover:bg-gray-700">
                <UserPlus size={20}/> Conferencia
              </button>
            </div>
          )}

          {secondCall && (
            <div className="flex items-center justify-between bg-purple-900/20 border border-purple-800/40 rounded-2xl px-4 py-3">
              <div className="flex items-center gap-2 text-sm">
                <Users size={16} className="text-purple-400"/>
                <span className="text-gray-200">{secondCall.number}</span>
                <span className="text-xs text-purple-400">
                  {secondCall.state === 'calling' ? 'Marcando...' : 'En conferencia'}
                </span>
              </div>
              <button onClick={endConference} aria-label="Terminar conferencia" className="p-1.5 rounded-full text-muted hover:text-red-400 hover:bg-gray-800 transition-colors">
                <X size={16}/>
              </button>
            </div>
          )}

          <div className="flex gap-2">
            {callState === 'idle' ? (
              <>
                <button onClick={() => setDialNumber(function(p) { return p.slice(0,-1); })} disabled={!dialNumber}
                  aria-label="Borrar ultimo digito"
                  className="w-12 h-12 bg-gray-800 hover:bg-gray-700 disabled:opacity-30 text-gray-300 rounded-full flex items-center justify-center transition-colors">
                  <Delete size={18}/>
                </button>
                <button onClick={() => handleDial(dialNumber)}
                  disabled={!dialNumber}
                  className="flex-1 h-12 bg-green-700 hover:bg-green-800 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-full flex items-center justify-center gap-2 text-lg font-medium transition-colors">
                  <Phone size={22}/> Marcar
                </button>
              </>
            ) : (callState === 'calling' || callState === 'active' || callState === 'held') ? (
              <button onClick={hangup}
                className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center gap-2 text-base font-medium">
                <PhoneOff size={20}/> Colgar
              </button>
            ) : null}
          </div>
        </div>

        <div className="card flex flex-col" style={{ minHeight: 480 }}>
          <div className="flex items-center gap-1 p-1 mb-4 bg-gray-800/60 rounded-full">
            {[
              { id: 'recents', icon: Clock, label: 'Recientes' },
              { id: 'contacts', icon: Users, label: 'Contactos' },
              { id: 'audio', icon: SlidersHorizontal, label: 'Audio' },
              { id: 'config', icon: Wifi, label: 'Config. SIP' },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={['flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-full text-xs font-medium transition-colors',
                  tab === t.id ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-200'].join(' ')}>
                <t.icon size={13}/> {t.label}
              </button>
            ))}
          </div>

          {tab === 'recents' && (
            <div className="flex-1 overflow-y-auto divide-y divide-gray-800/50">
              {recents.length === 0 && (
                <div className="flex flex-col items-center justify-center h-32 text-muted">
                  <PhoneCall size={28} className="mb-2 opacity-50"/>
                  <p className="text-sm">Sin llamadas recientes</p>
                </div>
              )}
              {recents.map(function(r, i) { return <CdrRow key={i} row={r} onDial={handleDial}/>; })}
            </div>
          )}

          {tab === 'contacts' && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="px-4 pb-3">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"/>
                  <input aria-label="Buscar contacto" value={contactSearch} onChange={function(e) { setContactSearch(e.target.value); }}
                    placeholder="Buscar contacto..."
                    className="w-full bg-gray-800 border border-gray-700 rounded-full pl-8 pr-4 py-2.5 text-sm text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus:border-blue-600"/>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-gray-800/50">
                {filtered.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-32 text-muted">
                    <Users size={28} className="mb-2 opacity-50"/>
                    <p className="text-sm">Sin contactos</p>
                  </div>
                )}
                {filtered.map(function(c, i) { return <ContactRow key={i} contact={c} onDial={handleDial}/>; })}
              </div>
            </div>
          )}

          {tab === 'audio' && (
            <div className="flex-1 overflow-y-auto">
              <AudioSettings/>
            </div>
          )}

          {tab === 'config' && (
            <div className="flex-1 overflow-y-auto px-2">
              <SipConfigPanel credentials={credentials}/>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
